import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * Reusable Optimization Type Selector Component
 * Displays optimization types fetched from database
 * 
 * @param {string} optimizationType - Current selected type (key)
 * @param {Function} onChange - Handler when type changes
 * @param {boolean} disabled - Whether the menu button is disabled
 */
function OptimizationTypeSelector({ optimizationType, onChange, disabled = false }) {
  const menuRef = useRef(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once on mount
    if (fetchedRef.current) return;
    
    async function fetchTypes() {
      fetchedRef.current = true;
      try {
        const response = await authenticatedFetch('/api/optimization-types');
        const data = await response.json();
        if (data.success && data.types) {
          setTypes(data.types);
        }
      } catch (error) {
        console.error('Error fetching optimization types:', error);
        // Fallback to hardcoded types on error
        setTypes([
          { key: 'regular', title: 'Regular', description: 'Balanced compression with minimal quality loss' },
          { key: 'moderate', title: 'Moderate', description: 'Higher compression with visible size reduction' },
          { key: 'extreme', title: 'Extreme', description: 'Maximum compression for smallest size' }
        ]);
      } finally {
        setLoading(false);
      }
    }
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only fetch once on mount

  // Find the current selected type label
  const selectedType = types.find(t => t.key === optimizationType);
  const selectedTypeTitle = selectedType?.title || 'Regular';

  const handleTypeChange = (value) => {
    onChange(value);
    // Close the menu after selection by clicking outside or using available methods
    requestAnimationFrame(() => {
      const menu = menuRef.current;
      if (menu) {
        // Try to close the menu using available methods
        if (typeof menu.hide === 'function') {
          menu.hide();
        } else if (typeof menu.hideOverlay === 'function') {
          menu.hideOverlay();
        } else {
          // Fallback: simulate a click outside the menu to close it
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: globalThis
          });
          document.body.dispatchEvent(clickEvent);
        }
      }
    });
  };

  if (loading) {
    return (
      <s-button variant="secondary" disabled>Loading...</s-button>
    );
  }

  return (
    <>
      <s-button
        commandFor="optimization-type-menu"
        variant="secondary"
        disabled={disabled}
      >
        {selectedTypeTitle}
      </s-button>
      <s-menu 
        ref={menuRef}
        id="optimization-type-menu" 
        accessibilityLabel="Optimization type selection"
      >
        {types.map((type) => (
          <s-button
            key={type.key}
            onClick={() => handleTypeChange(type.key)}
            disabled={disabled || loading}
          >
            {type.title}
          </s-button>
        ))}
      </s-menu>
    </>
  );
}

export default OptimizationTypeSelector;

